import { useState, useEffect, useRef } from "react";
import { useProfile } from "../../../hooks/useProfile";
import { apiFetch } from "../../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const useAdvancementManagement = (language, showAlert, t) => {
  const { profile } = useProfile();

  // ─── Requests State ────────────────────────────────────────────────────────
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalPending: 0,
    totalApproved: 0,
    totalDisbursed: 0,
    totalCompleted: 0,
    totalRejected: 0,
    totalAmount: 0,
  });

  // ─── Modal State ───────────────────────────────────────────────────────────
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [repaymentHistory, setRepaymentHistory] = useState([]);

  // ─── Form State ───────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    status: '',
    approvalNotes: '',
    rejectionReason: '',
    disbursementMethod: 'bank_transfer',
    referenceNumber: '',
  });

  // ─── Active Tab ───────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('all');

  // ─── In-flight guard ───────────────────────────────────────────────────────
  // Prevents double-clicking Approve/Reject/Disburse from firing two PATCH
  // requests: the first one succeeds, the second one lands after the status
  // has already changed and comes back as an error — the user sees a
  // "failed" toast even though the action actually went through.
  const [isProcessing, setIsProcessing] = useState(false);

  // ─── Init ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchRequests(activeTab);
    fetchStats();
  }, [activeTab, language]);

  // ─── Notification deep-link (?advancementId=...) ──────────────────────────
  // Primary source is the URL query param set by notification navigation;
  // window.__notificationTarget is the legacy in-memory fallback.
  const focusConsumedRef = useRef(false);
  useEffect(() => {
    if (focusConsumedRef.current || requests.length === 0) return;
    const urlId = new URLSearchParams(window.location.search).get("advancementId");
    const legacyId = window.__notificationTarget?.advancementId;
    const targetId = urlId || legacyId;
    if (!targetId) return;
    const request = requests.find((r) => r.id === Number(targetId));
    if (request) {
      focusConsumedRef.current = true;
      setSelectedRequest(request);
      setShowDetailsModal(true);
      if (legacyId) window.__notificationTarget = null;
    }
  }, [requests]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const buildEndpoint = (status = 'all') => {
    const clientName = profile?.clientName || localStorage.getItem('clientName') || '';

    const params = new URLSearchParams();
    if (status !== 'all') params.set('status', status);
    params.set('clientName', clientName);

    // No `managerInfinityCode` here any more. Row scope is now resolved
    // server-side from the authenticated token (getAllAdvancementRequests in
    // advancementController.js) — the browser is no longer the authority on
    // which employees it may see, so sending it would be both ignored and
    // misleading. The old code only set it for callers with neither an admin
    // role nor an adminLevelId, which is exactly why a manager holding an
    // admin level received the entire company's requests.
    return `${BASE_URL}/api/advancement-requests?${params.toString()}`;
  };

  const langHeader = () => ({
    'Accept-Language': language === 'ar' ? 'ar' : 'en',
  });

  const formatCurrency = (amount) => {
    if (language === 'ar') {
      return `${(amount || 0).toLocaleString('ar-EG')} ${t.currencySuffix}`;
    }
    return `${t.currencyPrefix}${(amount || 0).toLocaleString()}`;
  };

  const getEmployeeName = (request) => {
    if (language === 'ar') {
      return (
        request.arabicName ||
        request.employee?.arabicName ||
        request.employee?.nameArabic ||
        request.employee?.name_ar ||
        request.employee?.sr ||
        request.employeeName ||
        request.employee?.name ||
        t.unknownEmployee
      );
    }
    return (
      request.employeeName ||
      request.employee?.name ||
      request.employee?.englishName ||
      request.employee?.sr ||
      t.unknownEmployee
    );
  };

  const getStatusText = (status) => {
    const map = {
      pending: t.pending,
      approved: t.approved,
      rejected: t.rejected,
      disbursed: t.disbursed,
      completed: t.completed,
    };
    return map[status] || status;
  };

  const getUrgencyText = (urgency) => {
    const map = { normal: t.normal, high: t.high, urgent: t.urgent };
    return map[urgency] || urgency || t.normal;
  };

  // ─── Fetch ─────────────────────────────────────────────────────────────────
  const fetchRequests = async (status) => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiFetch(buildEndpoint(status), {
        method: 'GET',
        headers: langHeader(),
      });
      if (!res.ok) throw new Error('Failed to fetch requests');
      const data = await res.json();
      const list = data?.data ?? data ?? [];
      const normalizedList = Array.isArray(list) ? list : [];
      setRequests(normalizedList);
      return normalizedList;
    } catch (err) {
      console.error('Failed to fetch requests:', err);
      setError(err?.message || 'Failed to load requests');
      setRequests([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await apiFetch(buildEndpoint('all'), {
        method: 'GET',
        headers: langHeader(),
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      const payload = await res.json();
      const all = payload?.data || payload || [];
      setStats({
        totalPending: all.filter((r) => r.status === 'pending').length,
        totalApproved: all.filter((r) => r.status === 'approved').length,
        totalDisbursed: all.filter((r) => r.status === 'disbursed').length,
        // These two were missing entirely -- the Completed/Rejected tabs were
        // hardcoded to a count of 0 in AdvancementManagementPage.jsx no matter
        // how many requests actually had those statuses.
        totalCompleted: all.filter((r) => r.status === 'completed').length,
        totalRejected: all.filter((r) => r.status === 'rejected').length,
        totalAmount: all.reduce((sum, r) => sum + (r.requestedAmount || 0), 0),
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchRequestById = async (requestId) => {
    try {
      const res = await apiFetch(
        `${BASE_URL}/api/advancement-requests/${requestId}`,
        { method: 'GET', headers: langHeader() }
      );
      if (!res.ok) throw new Error('Failed to fetch request');
      const data = await res.json();
      return data?.data || data || null;
    } catch (err) {
      console.error('Failed to fetch request by id:', err);
      return null;
    }
  };

  const fetchRepaymentHistory = async (requestId) => {
    try {
      const res = await apiFetch(
        `${BASE_URL}/api/advancement-requests/${requestId}/repayments`,
        { method: 'GET', headers: langHeader() }
      );
      if (!res.ok) throw new Error('Failed to fetch repayments');
      const data = await res.json();
      setRepaymentHistory(data?.data || data || []);
    } catch (err) {
      console.error('Failed to fetch repayment history:', err);
      setRepaymentHistory([]);
    }
  };

  // ─── Actions ───────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({
      status: '',
      approvalNotes: '',
      rejectionReason: '',
      disbursementMethod: 'bank_transfer',
      referenceNumber: '',
    });
  };

  const refreshAfterAction = async () => {
    const refreshedRequests = await fetchRequests(activeTab);
    await fetchStats();

    if (selectedRequest?.id) {
      const refreshedSelectedRequest = refreshedRequests.find(
        (request) => request.id === selectedRequest.id
      );

      if (refreshedSelectedRequest) {
        setSelectedRequest(refreshedSelectedRequest);
      }
    }

    setShowProcessModal(false);
    resetForm();
  };

  const handleApprove = async () => {
    if (isProcessing) return; // guard against double-click / double-submit
    setIsProcessing(true);
    try {
      const adminCode =
        profile?.infinityCode || localStorage.getItem('infinityCode');
      const res = await apiFetch(
        `${BASE_URL}/api/advancement-requests/${selectedRequest.id}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...langHeader() },
          body: JSON.stringify({
            status: 'approved',
            reviewedBy: adminCode,
            approvalNotes: formData.approvalNotes,
          }),
        }
      );
      if (!res.ok) throw new Error('Approval failed');
      showAlert(t.approve, t.requestApprovedSuccess, 'success', refreshAfterAction);
    } catch (err) {
      console.error('Approval failed:', err);
      showAlert(t.approvalFailed, err?.message || t.approvalFailed, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!formData.rejectionReason.trim()) {
      showAlert(t.reject, t.provideRejectionReason, 'error');
      return;
    }
    if (isProcessing) return; // guard against double-click / double-submit
    setIsProcessing(true);
    try {
      const adminCode =
        profile?.infinityCode || localStorage.getItem('infinityCode');
      const res = await apiFetch(
        `${BASE_URL}/api/advancement-requests/${selectedRequest.id}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...langHeader() },
          body: JSON.stringify({
            status: 'rejected',
            reviewedBy: adminCode,
            rejectionReason: formData.rejectionReason,
          }),
        }
      );
      if (!res.ok) throw new Error('Rejection failed');
      showAlert(t.reject, t.requestRejected, 'success', refreshAfterAction);
    } catch (err) {
      console.error('Rejection failed:', err);
      showAlert(t.rejectionFailed, err?.message || t.rejectionFailed, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDisburse = async () => {
    if (!formData.referenceNumber.trim()) {
      showAlert(t.disburse, t.provideReferenceNumber, 'error');
      return;
    }
    if (isProcessing) return; // guard against double-click / double-submit —
    // this is the root cause of the disburse "glitch then succeed" behavior:
    // a second click fired a second PATCH while the first was still in
    // flight; the first would disburse successfully and the second would
    // land after the status had already flipped, coming back as an error.
    setIsProcessing(true);
    try {
      const adminCode =
        profile?.infinityCode || localStorage.getItem('infinityCode');
      const res = await apiFetch(
        `${BASE_URL}/api/advancement-requests/${selectedRequest.id}/status`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...langHeader() },
          body: JSON.stringify({
            status: 'disbursed',
            reviewedBy: adminCode,
            disbursementMethod: formData.disbursementMethod,
            referenceNumber: formData.referenceNumber,
          }),
        }
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        // The backend now treats an already-disbursed request as an
        // idempotent success (200), so a plain !res.ok here is a genuine
        // failure — but still surface any server-provided message.
        throw new Error(payload?.message || 'Disbursement failed');
      }
      showAlert(t.disburse, t.solfaDisbursedSuccess, 'success', refreshAfterAction);
    } catch (err) {
      console.error('Disbursement failed:', err);
      showAlert(t.disbursementFailed, err?.message || t.disbursementFailed, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    // State
    requests,
    loading,
    error,
    stats,
    activeTab,
    setActiveTab,
    selectedRequest,
    setSelectedRequest,
    showProcessModal,
    setShowProcessModal,
    showDetailsModal,
    setShowDetailsModal,
    repaymentHistory,
    formData,
    setFormData,
    isProcessing,
    // Helpers
    getEmployeeName,
    getStatusText,
    getUrgencyText,
    formatCurrency,
    // Actions
    fetchRequests,
    fetchRequestById,
    fetchRepaymentHistory,
    handleApprove,
    handleReject,
    handleDisburse,
    resetForm,
  };
};

export default useAdvancementManagement;